import { countOf, WORDS } from '../lib/format';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { MapWidget } from '../map/MapWidget';
import { SkeletonMap } from '../ui';
import { farmLandSet } from '../data-source/api/cards';
import { useTrackData } from '../data/useTrackData';
import type { ContourMeta, Crop, Farm, Massif } from '../data/types';
import { format } from './model';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';

const m = defineMessages({
  passport: { id: 'board.farmMap.passport', defaultMessage: 'Паспорт хозяйства', description: 'Ссылка под картой: открыть паспорт хозяйства; рядом стрелка' },
  loadingLabel: { id: 'board.farmMap.loadingLabel', defaultMessage: 'землю хозяйства', description: 'Что грузится (винительный падеж), для экранного диктора у скелетона карты' },
  noData: { id: 'board.farmMap.noData', defaultMessage: 'Карта хозяйства: нет данных', description: 'Подзаголовок блока карты, когда у хозяйства нет контуров' },
  noDataNote: { id: 'board.farmMap.noDataNote', defaultMessage: 'Очередь называет работы на полях и записи проверки, а не контуры, поэтому подсветить землю по этой строке нечем. Земля хозяйства открывается его паспортом.', description: 'Пояснение под пустой картой хозяйства' },
  summary: { id: 'board.farmMap.summary', defaultMessage: 'Земля хозяйства: {contours}, {area}; работы сигнала на карте не выделены — очередь называет работы на полях', description: 'Подзаголовок карты хозяйства; {contours} — число контуров словом («12 контуров»), {area} — площадь с единицей («46,8 га»)' },
  area: { id: 'board.farmMap.area', defaultMessage: '{value} га', description: 'Площадь с единицей; {value} — число с одним знаком' },
  hint: { id: 'board.farmMap.hint', defaultMessage: 'Нажмите на контур для краткой карточки. Площадь — из землепользований хозяйства, как в паспорте.', description: 'Подсказка под картой хозяйства' },
});

/**
 * Земля хозяйства на карте разбора ситуации (канон).
 *
 * Очередь ситуаций называет обязательства, а не контуры, поэтому подсветить
 * «работы сигнала» нечем. Но землю хозяйства канон знает: тот же набор, что
 * у паспорта `/farms/:id` (`farm-land-<id>`, все землепользования субъекта).
 * Рисуем её тем же кадром карты, площадь — сумма из канона; пока контуров
 * нет — прежняя фраза.
 */
export function FarmLandMap({ farmId, name, href, crops, farms, massifs, onSelect }: {
  farmId: string; name: string; href: string; crops: Crop[]; farms: Farm[]; massifs: Massif[];
  onSelect: (c: ContourMeta) => void;
}) {
  const t = useT();
  const land = useTrackData<ContourMeta[]>(farmLandSet(farmId));
  const rows = land.data ?? [];
  const ids = rows.map(c => c.id);
  const area = rows.reduce((s, c) => s + (c.area_stated === false ? 0 : c.area_ha), 0);
  const passport = <Link className="astra-link" to={href}>{t(m.passport)} <ArrowUpRight size={14} /></Link>;
  if (land.state === 'loading') return <div className="astra-situation-map" data-testid="farm-land-loading"><div><h3>{name}</h3></div><SkeletonMap label={t(m.loadingLabel)} height={320} /></div>;
  if (!ids.length) return <div className="astra-situation-map"><div><h3>{name}</h3><p>{t(m.noData)}</p></div><p className="astra-footnote">{t(m.noDataNote)}</p>{passport}</div>;
  return <div className="astra-situation-map"><div><h3>{name}</h3><p>{t(m.summary, { contours: countOf(ids.length, WORDS.contour), area: t(m.area, { value: format(area, 1) }) })}</p></div>
    <MapWidget colorBy="crop" filterIds={ids} focusIds={ids} crops={crops} height={320} legend="below" resolveNames={{ farm: id => farms.find(f => f.id === id)?.name, massif: id => massifs.find(m => m.id === id)?.name }} onSelect={c => { if (c) onSelect(c); }} card="none" />
    <p className="astra-footnote">{t(m.hint)}</p>{passport}</div>;
}
